import { numCompare, SavingState, toPercentageString, toStringSmartly } from '@common/Common';
import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue';
import { ProjectAcceptance } from '@common/types';
import { KendoChart, KendoChartDataSerie, KendoChartOptions } from '@Kendo/Components/KendoChart';
import { ICommandBase } from "@Quest/cqrs";
import PortfolioScenarioNavigation from '@Quest/PortfolioScenarios/Components/PortfolioScenarioNavigation.vue';
import ProjectsSelectionView from "@Quest/PortfolioScenarios/Components/ProjectsSelectionView.vue";
import { OptimizeSelectionCommandV1, UpdatePortfolioScenarioCommandV1, UpdateScenarioAcceptancesCommandV1 } from '@Quest/PortfolioScenarios/Domain/commands-core';
import PortfolioScenariosCommands from '@Quest/PortfolioScenarios/Domain/commands-vue-component';
import { ProjectAcceptanceDTO } from "@Quest/PortfolioScenarios/Domain/dtos";
import PortfolioScenariosQueries from '@Quest/PortfolioScenarios/Domain/queries-vue-component';
import { SelectionCostConstraintReadModel, SelectionCriterionReadModel, SelectionPageReadModel, SelectionProjectReadModel, SelectionResourceConstraintReadModel } from '@Quest/PortfolioScenarios/Domain/readmodels';
import Vue from "vue";
import Component, { mixins } from 'vue-class-component';
import { Route } from "vue-router";


@Component({
    components: {
        PortfolioNavigation, ProjectsSelectionView, PortfolioScenarioNavigation
    }
})
export default class SelectionPage extends mixins(PortfolioScenariosQueries, PortfolioScenariosCommands)
{
    get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId); }
    get tchProjectId(): number { return Number.parseInt(this.$route.params.tchProjectId); }
    get scenarioId() { return this.$route.params.scenarioId; }
    get portfolioId()
    {
        if (!this.rm?.PortfolioId)
        {
            throw new Error('PortfolioId is not defined.');
        }
        return this.rm.PortfolioId;
    }

    rm: SelectionPageReadModel | null = null;
    projects: SelectionProjectReadModel[] | null = null;

    searchPhrase: string | null = null;
    projectsFilter: string = 'All';
    criteriaSectionExpanded: boolean = false;
    constraintsSectionExpanded: boolean = false;
    resourcesSectionExpanded: boolean = false;

    savingState: SavingState = SavingState.Saved;
    autoSaveHandle: NodeJS.Timeout | null = null;

    EfficientFrontier: EfficientFrontierChart = new EfficientFrontierChart();
    PrioritizationMatrix: PrioritizationMatrixChart = new PrioritizationMatrixChart();

    get filteredProjects(): SelectionProjectReadModel[]
    {
        let view = new Array(...this.projects ?? []);

        if (this.searchPhrase)
        {
            const phrase = this.searchPhrase.toLowerCase();
            view = view.filter(a => a.Name.toLowerCase().indexOf(phrase) > -1);
        }

        switch (this.projectsFilter)
        {
            case 'All':
                return view;

            case 'Accepted':
                return view.filter(a => a.UiAcceptance === ProjectAcceptance.Accepted);

            case 'Rejected':
                return view.filter(a => a.UiAcceptance === ProjectAcceptance.Rejected);

            case 'TBD':
                return view.filter(a => a.UiAcceptance === ProjectAcceptance.TBD);

            default:
        }

        return [];
    }

    get topCriteria(): SelectionCriterionReadModel[] { return this.rm!.Criteria.filter(a => a.IsTop === true); }

    fmtNum(val: number | null) { return toStringSmartly(val); }

    fmtPct(val: number | null) { return toPercentageString(val); }

    getResourceCardClass(r: SelectionResourceConstraintReadModel)
    {
        if (r.Limit == null) return '';
        const ratio = r.Allocation / r.Limit;
        if (ratio > 1) return 'over-limit';
        if (ratio > 0.85) return 'near-limit';
        return '';
    }

    getCostCardClass(r: SelectionCostConstraintReadModel)
    {
        if (r.Limit == null) return '';
        const ratio = r.Allocation / r.Limit;
        if (ratio > 1) return 'over-limit';
        if (ratio > 0.85) return 'near-limit';
        return '';
    }



    handleAcceptSelection(p: SelectionProjectReadModel)
    {
        if (p.UiAcceptance === ProjectAcceptance.Accepted)
        {
            p.UiAcceptance = ProjectAcceptance.TBD;
        }
        else
        {
            p.UiAcceptance = ProjectAcceptance.Accepted;
        }

        this.savingState = SavingState.Pending;
    }

    handleRejectSelection(p: SelectionProjectReadModel)
    {
        if (p.UiAcceptance === ProjectAcceptance.Rejected)
        {
            p.UiAcceptance = ProjectAcceptance.TBD;
        }
        else
        {
            p.UiAcceptance = ProjectAcceptance.Rejected;
        }

        this.savingState = SavingState.Pending;
    }

    handleLockSelection(p: SelectionProjectReadModel)
    {
        p.UiAcceptanceLocked = true;
        this.savingState = SavingState.Pending;
    }

    handleUnlockSelection(p: SelectionProjectReadModel)
    {
        p.UiAcceptanceLocked = false;
        this.savingState = SavingState.Pending;
    }


    handleUpdateScenarioAcceptancesCommand()
    {
        if (this.savingState === SavingState.Pending)
        {
            const acceptances: ProjectAcceptanceDTO[] = this.projects
                .filter(a => a.UiAcceptance !== a.DbAcceptance || a.UiAcceptanceLocked !== a.DbAcceptanceLocked)
                .map(a =>
                {
                    const x = new ProjectAcceptanceDTO();
                    x.Acceptance = a.UiAcceptance;
                    x.Id = a.Id;
                    x.Lock = a.UiAcceptanceLocked;
                    return x;
                });

            if (acceptances.length)
            {
                this.invokeUpdateScenarioAcceptancesCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, acceptances);
                this.savingState = SavingState.InProgress;
            }
        }
    }


    mounted()
    {
        document.title = "Portfolio Plan - Selection";
        this.runSelectionPageQueryV1(this.workspaceId, this.tchProjectId, this.scenarioId);

        this.autoSaveHandle = setInterval(() => this.handleUpdateScenarioAcceptancesCommand(), 10000);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName == "SelectionPageReadModel")
        {
            this.rm = model as SelectionPageReadModel;

            if (this.projects == null)
            {
                this.projects = this.rm.Projects;
            }

            this.EfficientFrontier.SetData(this.projects, this.rm.CurrencyLabel);
            this.PrioritizationMatrix.SetData(this.projects, this.rm.CurrencyLabel);

            Vue.nextTick(() =>
            {
                this.EfficientFrontier.Render();
                this.PrioritizationMatrix.Render();
            })
        }
    }

    onCommandSucceed(cmd: ICommandBase, correlationId: string)
    {
        if (OptimizeSelectionCommandV1.matchType(cmd))
        {
            this.projects = null;
            this.runSelectionPageQueryV1(this.workspaceId, this.tchProjectId, this.scenarioId);
        }
        else if (UpdatePortfolioScenarioCommandV1.matchType(cmd))
        {
            this.projects = null;
            this.runSelectionPageQueryV1(this.workspaceId, this.tchProjectId, this.scenarioId);
        }
        else if (UpdateScenarioAcceptancesCommandV1.matchType(cmd))
        {
            if (this.savingState === SavingState.InProgress)
            {
                this.savingState = SavingState.Saved;
            }

            const cmd1 = cmd as UpdateScenarioAcceptancesCommandV1;

            for (const acc of cmd1.Acceptances)
            {
                const project = this.projects.find(p => p.Id === acc.Id);

                if (project)
                {
                    project.DbAcceptance = acc.Acceptance as ProjectAcceptance;
                    project.DbAcceptanceLocked = acc.Lock;
                }
            }

            this.runSelectionPageQueryV1(this.workspaceId, this.tchProjectId, this.scenarioId);
        }
    }

    beforeDestroy()
    {
        if (this.autoSaveHandle)
        {
            clearInterval(this.autoSaveHandle);
        }
    }

    beforeRouteLeave(to: Route, from: Route, next: (arg?: any) => void) 
    {
        this.handleUpdateScenarioAcceptancesCommand();
        next();
    }
}

class EfficientFrontierChart 
{
    isRendered: boolean = false;
    
    Chart: KendoChart;

    constructor()
    {
        const options = new KendoChartOptions();
        options.HideLegend();
        options.SetPannable(true);
        options.SetZoomable(true);
        options.SetTooltip(false, '#= dataItem.Name #');
        //options.SetOnSeriesHover(this.page.Highlight);
        //options.SetOnSeriesClick(this.page.OnSeriesClick);

        const chart = KendoChart.CreateScatterChart(options);
        this.Chart = chart;
    }

    SetData(projects: SelectionProjectReadModel[], currencyLabel: string)
    {

        let dataItems: { AlternativeId: string, Name: string, Acceptance: ProjectAcceptance, Color: string | null, X: number, Y: number, Z: number, TooltipHtml: string }[] = [];

        for (const alternativeVM of projects)
        {
            const xValue = alternativeVM.TotalCost;
            const yValue = alternativeVM.TotalValue;
            const zValue = alternativeVM.CostOfValuePoint;

            dataItems.push({
                Name: alternativeVM.Name,
                X: xValue as number,
                Y: yValue as number,
                Z: zValue as number,
                AlternativeId: alternativeVM.Id,
                Acceptance: alternativeVM.DbAcceptance,
                Color: null,
                TooltipHtml: `${alternativeVM.Name}`
            });
        }

        dataItems = dataItems.filter(a => a.X != null && a.Y != null && a.Z != null).sort((a, b) => numCompare(a.Z, b.Z));

        for (let i = 1; i < dataItems.length; i++) //from 1 to skip first
        {
            dataItems[i].X += dataItems[i - 1].X;
            dataItems[i].Y += dataItems[i - 1].Y;
        }

        const accepted = new KendoChartDataSerie();
        accepted.SetXField("X");
        accepted.SetYField("Y");
        accepted.SetCategoryField("Name");
        //serie.SetColorField("Color");
        accepted.SetColor("#28a745");
        accepted.SetMarkers(true, "circle"); // circle, square, triangle, cross
        accepted.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.Accepted));

        const rejected = new KendoChartDataSerie();
        rejected.SetXField("X");
        rejected.SetYField("Y");
        rejected.SetCategoryField("Name");
        //serie.SetColorField("Color");
        rejected.SetColor("#dc3545");
        rejected.SetMarkers(true, "cross"); // circle, square, triangle, cross
        rejected.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.Rejected));

        const tbd = new KendoChartDataSerie();
        tbd.SetXField("X");
        tbd.SetYField("Y");
        tbd.SetCategoryField("Name");
        //serie.SetColorField("Color");
        tbd.SetColor("#FFA500");
        tbd.SetMarkers(true, "triangle"); // circle, square, triangle, cross
        tbd.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.TBD));

        const series: KendoChartDataSerie[] = [accepted, rejected, tbd];


        const options = this.Chart.GetOptions();
        options.SetTooltip(true, "#= dataItem.TooltipHtml #");

        const xMaxValue = Math.max(...dataItems.map(v => v.X));
        const yMaxValue = Math.max(...dataItems.map(v => v.Y));

        const xAxisMin = 0;
        const xAxisMax = xMaxValue * 1.1;
        const yAxisMin = 0;
        const yAxisMax = yMaxValue * 1.1;

        const xAxisLabelFormat = xMaxValue > 10 ? "{0:n0}" : null;
        const yAxisLabelFormat = yMaxValue > 10 ? "{0:n0}" : null;

        const xCrosshairTooltipTemplate = xMaxValue ? (e: any): string => kendo.toString(e.value / xMaxValue, "p0") : undefined;
        const yCrosshairTooltipTemplate = yMaxValue ? (e: any): string => kendo.toString(e.value / yMaxValue, "p0") : undefined;

        options.SetXAxis(null, xAxisMin, xAxisMax, `Cost (${currencyLabel})`, xAxisLabelFormat, 2, true, true, null, xCrosshairTooltipTemplate);
        options.SetYAxis(null, yAxisMin, yAxisMax, "Value", yAxisLabelFormat, true, true, null, yCrosshairTooltipTemplate);

        this.Chart.SetOptions(options);
        this.Chart.SetDataSeries(series);

        this.Chart.UpdateOptions();
    }

    Render()
    {
        if (this.isRendered === false)
        {
            this.Chart.Render1("EfficientFrontier");
            this.isRendered = true;
        }
    }
}

class PrioritizationMatrixChart 
{
    isRendered: boolean = false;

    Chart: KendoChart;

    constructor()
    {
        const options = new KendoChartOptions();
        options.HideLegend();
        options.SetPannable(true);
        options.SetZoomable(true);
        options.SetTooltip(false, '#= dataItem.Name #');
        //options.SetOnSeriesHover(this.page.Highlight);
        //options.SetOnSeriesClick(this.page.OnSeriesClick);

        const chart = KendoChart.CreateScatterChart(options);
        this.Chart = chart;
    }

    SetData(projects: SelectionProjectReadModel[], currencyLabel: string)
    {
        let dataItems: { AlternativeId: string, Name: string, Acceptance: ProjectAcceptance, Color: string | null, X: number, Y: number, TooltipHtml: string }[] = [];

        for (const alternativeVM of projects)
        {
            const xValue = alternativeVM.TotalCost;
            const yValue = alternativeVM.TotalValue;

            dataItems.push({
                Name: alternativeVM.Name,
                X: xValue as number,
                Y: yValue as number,
                AlternativeId: alternativeVM.Id,
                Acceptance: alternativeVM.DbAcceptance,
                Color: null,
                TooltipHtml: `${alternativeVM.Name}`
            });
        }

        dataItems = dataItems.filter(a => a.X != null && a.Y != null);

        const accepted = new KendoChartDataSerie();
        accepted.SetXField("X");
        accepted.SetYField("Y");
        accepted.SetCategoryField("Name");
        //serie.SetColorField("Color");
        accepted.SetColor("#28a745");
        accepted.SetMarkers(true, "circle"); // circle, square, triangle, cross
        accepted.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.Accepted));

        const rejected = new KendoChartDataSerie();
        rejected.SetXField("X");
        rejected.SetYField("Y");
        rejected.SetCategoryField("Name");
        //serie.SetColorField("Color");
        rejected.SetColor("#dc3545");
        rejected.SetMarkers(true, "cross"); // circle, square, triangle, cross
        rejected.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.Rejected));

        const tbd = new KendoChartDataSerie();
        tbd.SetXField("X");
        tbd.SetYField("Y");
        tbd.SetCategoryField("Name");
        //serie.SetColorField("Color");
        tbd.SetColor("#FFA500");
        tbd.SetMarkers(true, "triangle"); // circle, square, triangle, cross
        tbd.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.TBD));

        const series: KendoChartDataSerie[] = [accepted, rejected, tbd];

        const options = this.Chart.GetOptions();
        options.SetTooltip(true, "#= dataItem.TooltipHtml #");

        const xMaxValue = Math.max(...dataItems.map(v => v.X));
        const yMaxValue = Math.max(...dataItems.map(v => v.Y));

        const xAxisMin = 0;
        const xAxisMax = xMaxValue * 1.1;
        const yAxisMin = 0;
        const yAxisMax = yMaxValue * 1.1;

        const xAxisLabelFormat = xMaxValue > 10 ? "{0:n0}" : null;
        const yAxisLabelFormat = yMaxValue > 10 ? "{0:n0}" : null;

        options.SetXAxis(null, xAxisMin, xAxisMax, `Cost (${currencyLabel})`, xAxisLabelFormat, 2, true, false, null);
        options.SetYAxis(null, yAxisMin, yAxisMax, "Value", yAxisLabelFormat, true, false, null);

        this.Chart.SetOptions(options);
        this.Chart.SetDataSeries(series);

        this.Chart.UpdateOptions();
    }

    Render()
    {
        if (this.isRendered === false)
        {
            this.Chart.Render1("PrioritizationMatrix");
            this.isRendered = true;
        }
    }
}